import { PeriodType } from "./shared/entities/payment";
import { CheckPurchasedProductsResponse } from "./shared/entities/subscription";
import { PayableEntity } from "@solidgate/react-sdk";
import { IStreakPeriod, IWeekDay, StreakType, WEEKDAY_ABBREVIATIONS } from "./shared/entities/streak";
import { getMainHostUrl } from "../utils/getMainHostUrl";
import { resolveLanguageCode } from "i18n/resolve-language";
import {
    addDays,
    differenceInCalendarDays,
    eachDayOfInterval,
    format,
    isAfter,
    isSameDay,
    isToday,
    parseISO,
    startOfWeek
} from "date-fns";
import { UserOnboardingAnswer } from "./shared/entities/student";

export const parseQueryParams = (query: string): Record<string, string> => {
    const params = new URLSearchParams(query);
    const result: Record<string, string> = {};
    params.forEach((value, key) => {
        result[key] = value;
    });
    return result;
};

export function getGA4ClientId() {
    const cookies = document.cookie.split("; ");

    const gaCookie = cookies.find((cookie) => cookie.startsWith("_ga=") && !cookie.includes("amplitude"));

    if (gaCookie) {
        const gaValue = gaCookie.split("=")[1];
        const parts = gaValue.split(".");

        if (parts.length >= 4) {
            return `${parts[2]}.${parts[3]}`;
        }
    }

    return "";
}

export function getFacebookCookies() {
    const cookies = document.cookie.split("; ");

    const fbpCookie = cookies.find((cookie) => cookie.startsWith("_fbp="));
    const fbcCookie = cookies.find((cookie) => cookie.startsWith("_fbc="));

    const _fbp = fbpCookie ? fbpCookie.split("=")[1] : "";
    const _fbc = fbcCookie ? fbcCookie.split("=")[1] : "";

    return { _fbp, _fbc };
}

export function calculateDailyPrice(price: number, period: number, periodType: PeriodType): number {
    const daysPerUnit: Record<PeriodType, number> = {
        D: 1,
        W: 7,
        M: 30,
        Q: 90,
        Y: 365
    };

    const totalDays = period * daysPerUnit[periodType];

    return price / 100 / totalDays;
}

export const getPeriodLabel = (type: PeriodType): string => {
    switch (type) {
        case "D":
            return "day";
        case "W":
            return "week";
        case "M":
            return "month";
        case "Q":
            return "quarter";
        case "Y":
            return "year";
    }
};

export const getMonthAfterThreeMonths = () => {
    const date = new Date();
    date.setMonth(date.getMonth() + 3);

    const monthName = date.toLocaleString("en-US", { month: "long" });
    return monthName.charAt(0).toUpperCase() + monthName.slice(1);
};

export const getCurrentYear = () => {
    return new Date().getFullYear();
};

export const formatDate = (date: Date, language?: string | null) => {
    return date.toLocaleDateString(resolveDisplayLocale(language), {
        month: "short",
        day: "numeric"
    });
};

export const formatFullDate = (
    isoDate: string,
    monthType: "short" | "long",
    language?: string | null
) => {
    if (!isoDate) return "";

    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return "";

    return date.toLocaleDateString(resolveDisplayLocale(language), {
        day: "numeric",
        month: monthType,
        year: "numeric"
    });
};

export const formatfullDate = (isoDate: string): string => {
    if (!isoDate) return "";

    const date = new Date(isoDate);

    if (isNaN(date.getTime())) return "";

    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month}.${day}.${year}`;
};

export const currencySymbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    INR: "₹",
    AUD: "A$",
    CAD: "C$",
    GBP: "£",
    BRL: "R$",
    // The currencies the Solidgate catalog prices in since 2026-10. The Gulf
    // currencies and the Colombian peso go by their ISO code, as the funnel
    // checkout shows them (the local signs are Arabic script or a bare "$"
    // that reads as dollars); the peso, sol and yen have Latin signs. The
    // space after a code (and the sol) is a NO-BREAK space: "KWD 15.50" is one
    // token, so a narrow pill wraps before or after the amount, never between
    // the code and the number.
    JPY: "¥",
    MXN: "MX$",
    PEN: "S/\u00A0",
    SAR: "SAR\u00A0",
    AED: "AED\u00A0",
    KWD: "KWD\u00A0",
    OMR: "OMR\u00A0",
    JOD: "JOD\u00A0",
    COP: "COP\u00A0"
};

// Currencies whose amounts read as whole units: the ".00" tail is dropped and
// a synthetic strikethrough is rounded to the unit (₹1899, ¥6290, COP 125900).
const WHOLE_UNIT_CURRENCIES = new Set(["INR", "JPY", "COP"]);
export const showsWholeUnits = (currency: string): boolean =>
    WHOLE_UNIT_CURRENCIES.has((currency || "").trim().toUpperCase());

// Solidgate's SDK and webhooks report an order amount in the currency's
// SMALLEST unit (1090 JPY = ¥1090, 12400 KWD = 12.400 KWD), while every price
// in this app — the catalog the backend serves, the analytics events, the
// pending-order storage — is in hundredths. Convert an SDK amount before it
// meets a `/ 100`. Mirrors app-backend payments/solidgate_units.go.
const SOLIDGATE_CURRENCY_EXPONENT: Record<string, number> = {
    JPY: 0, KRW: 0, VND: 0, CLP: 0, ISK: 0,
    KWD: 3, OMR: 3, JOD: 3, BHD: 3, TND: 3
};
export function solidgateMinorToHundredths(amount: number, currency: string | null | undefined): number;
export function solidgateMinorToHundredths(amount: number | null | undefined, currency: string | null | undefined): number | undefined;
export function solidgateMinorToHundredths(amount: number | null | undefined, currency: string | null | undefined): number | undefined {
    if (amount === null || amount === undefined) return undefined;
    const exponent = SOLIDGATE_CURRENCY_EXPONENT[(currency || "").trim().toUpperCase()] ?? 2;
    if (exponent === 0) return amount * 100;
    if (exponent === 3) return Math.round(amount / 10);
    return amount;
}

// UI-only conversion of a dollar list price into a currency whose price list
// has no hand-set figure: the strikethroughs, the "usual fee" tables and the
// gift anchors of the upsell screens. The ratios follow the Solidgate price
// list of 2026-10 (39.99 USD = 149.90 SAR = 12.400 KWD = 6,290 JPY …) and the
// result is rounded to the step such a list uses. Nothing is charged off
// these: the charged price is always the plan's own. A currency without a
// ratio returns null and the caller keeps its dollar figure, as before.
const USD_TO_LOCAL_APPROX: Record<string, { rate: number; step: number }> = {
    SAR: { rate: 3.75, step: 0.1 },
    AED: { rate: 3.67, step: 0.1 },
    KWD: { rate: 0.31, step: 0.05 },
    OMR: { rate: 0.385, step: 0.05 },
    JOD: { rate: 0.71, step: 0.05 },
    MXN: { rate: 18.7, step: 1 },
    COP: { rate: 3150, step: 100 },
    PEN: { rate: 3.48, step: 0.1 },
    JPY: { rate: 157, step: 10 }
};

export const approxLocalPrice = (usdPrice: number, currency: string): number | null => {
    const entry = USD_TO_LOCAL_APPROX[(currency || "").trim().toUpperCase()];
    if (!entry || !Number.isFinite(usdPrice)) return null;
    const stepped = Math.round((usdPrice * entry.rate) / entry.step) * entry.step;
    return Math.round(stepped * 100) / 100;
};

/**
 * A per-currency list price (major units): the hand-set figure for the
 * currency, else the dollar one converted by `approxLocalPrice`, else null.
 */
export const localListPrice = (prices: Record<string, number>, currency: string): number | null => {
    const code = (currency || "").trim().toUpperCase();
    const listed = prices[code];
    if (typeof listed === "number") return listed;
    return typeof prices.USD === "number" ? approxLocalPrice(prices.USD, code) : null;
};

export function getCurrencyByCountryCode(code: string): string {
    switch (code) {
        case "GB":
            return "GBP";
        case "CA":
            return "CAD";
        case "AU":
            return "AUD";
        case "US":
            return "USD";
        case "IN":
            return "INR";
        case "AL":
        case "AD":
        case "AT":
        case "BE":
        case "BG":
        case "HR":
        case "CY":
        case "CZ":
        case "DK":
        case "EE":
        case "FI":
        case "FR":
        case "DE":
        case "GR":
        case "HU":
        case "IE":
        case "IT":
        case "LV":
        case "LT":
        case "LU":
        case "MC":
        case "MD":
        case "ME":
        case "MT":
        case "NL":
        case "NO":
        case "PL":
        case "PT":
        case "RO":
        case "RS":
        case "SM":
        case "SK":
        case "SI":
        case "ES":
        case "SE":
        case "CH":
        case "IS":
        case "LI":
        case "MK":
        case "UA":
        case "VA":
        case "BA":
        case "BY":
        case "XK":
        case "TR":
        case "RU":
        case "GI":
        case "GG":
        case "JE":
        case "IM":
        case "AX":
        case "FO":
        case "SJ":
            return "EUR";
        default:
            return "USD";
    }
}

// Spain + Latin America follow the local convention of prefixing USD as "US$";
// everywhere else worldwide shows a bare "$". Driven by the user's COUNTRY (the
// IP-country of the purchased plan), not the UI language — same set as
// app-funnels' US_DOLLAR_PREFIX_COUNTRIES; keep the two in sync.
export const US_DOLLAR_PREFIX_COUNTRIES = new Set<string>([
    "ES", // Spain
    // Latin America
    "MX", "GT", "HN", "SV", "NI", "CR", "PA", "CU", "DO", "PR",
    "CO", "VE", "EC", "PE", "BO", "CL", "AR", "PY", "UY", "BR"
]);

export const usesUsDollarPrefix = (country?: string): boolean =>
    !!country && US_DOLLAR_PREFIX_COUNTRIES.has(country.toUpperCase());

// The purchase-time IP-country of the student's subscription, set once the
// subscriptions load (features/subscription/model). Upsell prices render only
// after the plan catalog resolves, which itself waits on the subscriptions
// (payment system is derived from them), so getCurrencySymbol call sites that
// don't pass `country` explicitly still resolve the correct "US$"/"$" form
// through this fallback. Filled only client-side, after the initial render, so
// SSR markup and hydration stay consistent.
let knownUserCountry = "";
export const setKnownUserCountry = (country: string): void => {
    knownUserCountry = (country || "").toUpperCase();
};

// Second-tier fallback: the CURRENT GeoIP country. subscription.country is a
// backend join that is often empty (most truegate subscriptions, solidgate
// from early 2025), so when the loaded subscriptions carry no country the
// subscription model fetches the live location instead and stores it here.
// Kept separate from knownUserCountry so arrival order never matters: the
// purchase-time country always wins once it is known.
let geoUserCountry = "";
export const setGeoUserCountry = (country: string): void => {
    geoUserCountry = (country || "").toUpperCase();
};

export const getCurrencySymbol = (code: string, lang?: string, country?: string): string => {
    const upperCode = code?.toUpperCase();
    const symbol = currencySymbols[upperCode] || "";

    if (upperCode === "USD" && symbol === "$") {
        // Country is the source of truth once known: the explicit argument
        // first, then the purchase-time country, then the live GeoIP country.
        // The UI language only kicks in while no country is known at all.
        const effectiveCountry = country || knownUserCountry || geoUserCountry;
        if (effectiveCountry) return usesUsDollarPrefix(effectiveCountry) ? "US$" : "$";
        if (lang === "es") return "US$";
    }

    return symbol;
};

/**
 * Wrap a value in a bidi isolate (LRI … PDI) before dropping it into a sentence.
 * Inside Arabic copy the digits of "€29.99" resolve to an Arabic number, which
 * leaves the neutral "€" to drift to the far end of the line — the price renders
 * as "29.99€", or worse, with the symbol several words away. The isolate pins the
 * whole token into one left-to-right run. Visually a no-op in LTR locales.
 * Only needed where the value is interpolated into text; a price rendered in its
 * own element is already a separate run.
 */
export const bidiIsolate = (value: string): string => `⁦${value}⁩`;

export const formatNumberToK = (num: number) => {
    if (num < 1000) {
        return `${Number((num / 1000).toFixed(1))}K`;
    }

    if (num % 1000 === 0) {
        return `${num / 1000}K`;
    }

    return `${Number((num / 1000).toFixed(1))}K`;
};

// Locale tokens accepted inside a provider plan name, e.g.
// "4-WEEK PLAN{es: PLAN DE 4 SEMANAS, pt_br: PLANO DE 4 SEMANAS}".
// A locale missing here is ignored by extractFromBraces even when the provider sends it.
export const PLAN_NAME_LOCALES = ["en", "es", "de", "fr", "it", "ja", "pt_br", "ar"] as const;

// "pt-BR" and a plain "pt" both reach the "pt_br" token — no plan name carries a "pt" one.
export const resolvePlanNameLanguage = (value?: string | null): string | undefined =>
    resolveLanguageCode(value, PLAN_NAME_LOCALES) ?? undefined;

/**
 * Our locale codes use an underscore ("pt_br"), which is not a valid BCP 47 tag —
 * Intl throws `RangeError: Invalid language tag` on it. Convert to the hyphenated
 * form with an uppercase region: "pt_br" -> "pt-BR".
 */
const toBcp47 = (locale: string): string => {
    const [language, region] = locale.split("_");
    return region ? `${language}-${region.toUpperCase()}` : language;
};

/** Arabic formats with Western digits: the product's numbers (prices, percentages) stay 0-9. */
const ARABIC_LATIN_DIGITS = "ar-u-nu-latn";

/** Locale for Intl date/number formatting. Always a valid BCP 47 tag. */
export const resolveDisplayLocale = (language?: string | null): string => {
    const fromPlanLocales = resolvePlanNameLanguage(language);
    if (fromPlanLocales) return fromPlanLocales === "ar" ? ARABIC_LATIN_DIGITS : toBcp47(fromPlanLocales);

    if (!language) return "en";

    const trimmed = language.trim();
    if (!trimmed) return "en";

    return trimmed.split(/[-_]/)[0] || "en";
};

export const formatBillingTransactionDate = (value: Date | string, language?: string | null): string => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const formatted = new Intl.DateTimeFormat(resolveDisplayLocale(language), {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "UTC"
    }).format(date);

    return `${formatted} UTC`;
};

export const formatPaymentStatus = (
    status: string,
    t: (phrase: string, opts?: { fallback?: string }) => string
): string => {
    const trimmed = status?.trim();
    if (!trimmed) return status;
    return t(trimmed, { fallback: trimmed });
};

export const extractFromBraces = (input: string, lang?: string) => {
    const match = input.match(/{([^}]+)}/);
    if (!match) return input;

    const content = match[1];
    const result: Record<string, string> = {};
    const pairRegex = new RegExp(`(${PLAN_NAME_LOCALES.join("|")}):\\s*([^,}]+)`, "g");

    for (const pairMatch of content.matchAll(pairRegex)) {
        const [, key, value] = pairMatch;
        if (key && value) {
            result[key] = value.trim();
        }
    }

    if (Object.keys(result).length > 0) {
        if (lang && result[lang]) {
            return result[lang];
        }

        for (const locale of PLAN_NAME_LOCALES) {
            if (result[locale]) {
                return result[locale];
            }
        }

        return Object.values(result)[0] ?? content;
    }

    // {3-MONTH PLAN}
    return content;
};

export const calculateDiscountPercentage = (price: number, discount: number): number => {
    if (price <= 0) {
        throw new Error("price error (should be >= 0)");
    }
    const percentage = (discount / price) * 100;
    return Math.round(percentage);
};

export const getPriceBeforeSale = (priceAfterSale: number, sale: number) => {
    return Math.round(priceAfterSale / ((100 - sale) / 100));
};

export const formatPrice = (price: number) => {
    return price % 1 === 0 ? price.toString() : price.toFixed(2);
};

export type UpsellStatus = "Success" | "Processing" | "Fail";

// Solidgate: `auth_ok` means the funds are reserved and product access can be granted
// (https://docs.solidgate.com/payments/card-payments/auto-settle/); `settle_ok` only
// confirms the later capture. The order status alone decides success: for card orders
// the backend fills transaction_status from the settle transaction, so it stays
// "none"/"processing" until capture and must not gate access.
const SUCCESS_ORDER_STATUSES = ["auth_ok", "settle_ok", "approved", "partial_settled", "free", "success"];
// `3ds_verify` is an unfinished 3D Secure challenge: nothing is authorised and
// the order expires into auth_failed if the user never completes it. It must
// not read as "Processing", which the pack pages treat as ownership; the open
// challenge itself is tracked by the upsell model, not by this status.
const FAIL_ORDER_STATUSES = ["auth_failed", "decline", "declined", "failed", "3ds_verify"];
const FAIL_TRANSACTION_STATUSES = ["fail", "decline", "error", "failed"];

// A Pix QR / UPI upsell order the customer has not paid yet: the QR or UPI
// request is out and may still be paid in the bank / UPI app, a Pix QR for up
// to 75 minutes. It grants nothing, so for ownership it reads as "Fail" — as
// "Processing" the pack pages would open the pack for an unpaid QR. It is no
// decline either: the upsell page waits it out (isUpsellAwaitingPayment) and the
// backend starts no second order of the product meanwhile.
export const UPSELL_AWAITING_PAYMENT_STATUS = "awaiting_payment";

export const isAwaitingPaymentOrderStatus = (orderStatus: string | null | undefined): boolean =>
    orderStatus?.trim().toLowerCase() === UPSELL_AWAITING_PAYMENT_STATUS;

export const checkUpsellStatus = (
    orderStatus: string,
    transactionStatus: string
): UpsellStatus => {
    const order = orderStatus.trim().toLowerCase();
    const transaction = transactionStatus.trim().toLowerCase();
    if (SUCCESS_ORDER_STATUSES.includes(order)) {
        return "Success";
    }

    if (
        FAIL_ORDER_STATUSES.includes(order) ||
        FAIL_TRANSACTION_STATUSES.includes(transaction) ||
        isAwaitingPaymentOrderStatus(order)
    ) {
        return "Fail";
    }

    return "Processing";
};

export const isUpsellAwaitingPayment = (data: CheckPurchasedProductsResponse, upsellId: string): boolean =>
    isAwaitingPaymentOrderStatus(data.productStatus[upsellId]?.orderStatus);

export function checkUpsellPurchased(
    data: CheckPurchasedProductsResponse,
    upsellId: string
): UpsellStatus {
    const isMarkedAsPurchased = data.purchasedProducts[upsellId];
    const status = data.productStatus[upsellId];

    if (status) {
        return checkUpsellStatus(status.orderStatus, status.transactionStatus);
    }

    if (isMarkedAsPurchased) {
        return "Success";
    }

    return "Fail";
}

export const formatTrialPeriod = (days: number) => {
    if (days % 7 === 0) {
        const weeks = days / 7;
        return `${weeks}-week${weeks > 1 ? "s" : ""}`;
    }
    return `${days}-day${days > 1 ? "s" : ""}`;
};

export const getPaymentMethod = (method: PayableEntity) => {
    switch (method) {
        case PayableEntity.Form:
            return "card";
        case PayableEntity.ApplePay:
            return "applepay";
        case PayableEntity.GooglePay:
            return "googlepay";
        case PayableEntity.Resign:
            return "resign";
        case PayableEntity.Paypal:
            return PayableEntity.Paypal;
        default:
            return method;
    }
};

export const getDevicePlatformForPayment = (): "ios" | "android" | "desktop" => {
    const userAgent = typeof window !== "undefined" ? window.navigator.userAgent : "";
    if (/iPhone|iPad|iPod/i.test(userAgent)) return "ios";
    if (/Android/i.test(userAgent)) return "android";
    return "desktop";
};

export const getDayStatus = (date: Date, streakDays: Set<string>, today: Date): StreakType => {
    const dayStr = format(date, "yyyy-MM-dd");
    const prevDayStr = format(addDays(date, -1), "yyyy-MM-dd");

    if (isAfter(date, today)) {
        return StreakType.DEFAULT;
    }

    const hasLesson = streakDays.has(dayStr);
    const prevHasLesson = streakDays.has(prevDayStr);

    if (hasLesson) {
        return StreakType.DONE;
    }

    if (isSameDay(date, today) && prevHasLesson) {
        return StreakType.TODAY;
    }

    if (!hasLesson && prevHasLesson) {
        return StreakType.BROKEN;
    }

    if (isSameDay(date, today)) {
        return StreakType.TODAY;
    }

    return StreakType.DEFAULT;
};

export const getLessonDays = (intervals: IStreakPeriod[] | null): Set<string> => {
    if (!intervals) return new Set<string>();

    const days = new Set<string>();

    intervals.forEach(({ StartDate, EndDate }) => {
        eachDayOfInterval({
            start: parseISO(StartDate),
            end: parseISO(EndDate)
        }).forEach((day) => {
            days.add(format(day, "yyyy-MM-dd"));
        });
    });

    return days;
};

export const buildWeek = (streakResponse: IStreakPeriod[] | null): IWeekDay[] => {
    const today = new Date();
    const streakDays = getLessonDays(streakResponse);
    const weekStart = startOfWeek(today, { weekStartsOn: 0 });

    return Array.from({ length: 7 }, (_, i) => {
        const date = addDays(weekStart, i);
        let status: StreakType;

        if (streakResponse) {
            status = getDayStatus(date, streakDays, today);
        } else {
            status = isSameDay(date, today) ? StreakType.TODAY : StreakType.DEFAULT;
        }

        return {
            date,
            label: WEEKDAY_ABBREVIATIONS[i],
            status
        };
    });
};

// Natural-key strings; interpolation ({{days}}) is resolved at the render site.
export const streakWidgetTitles: {
    type: StreakType;
    titles: string[];
}[] = [
        {
            type: StreakType.DONE,
            titles: [
                "Small steps, big compounding wins",
                "Quick task, big satisfaction",
                "{{days}}-day streak and still climbing",
                "Momentum unlocked"
            ]
        },
        {
            type: StreakType.TODAY,
            titles: [
                "Kick off your streak by wrapping up a lesson!",
                "Start your streak by finishing a lesson!",
                "Complete a lesson to keep your streak",
                "Finish a lesson to keep your streak going!"
            ]
        },
        {
            type: StreakType.BROKEN,
            titles: ["Streak's over, but hey, you can start fresh!", "Is it time to let go of the past and start fresh?"]
        }
    ];
export function mapErrorToMessage(code: string): string {
    const messages: Record<string, string> = {
        "0.01": "Payment declined by bank. Please try again.",
        "0.02": "Order expired. Please try again.",
        "0.03": "Please try another card.",
        "1.01": "Authentication failed. Please try again.",
        "2.01": "Invalid payment data. Please check your details.",
        "2.02": "Payment amount exceeds the limit. Please check card limits or contact your bank.",
        "2.03": "Invalid currency. Please use supported currency.",
        "2.05": "Order not found. Please check the order details.",
        "2.06": "Invalid CVV code. Please try again.",
        "2.07": "Incomplete payment information. Please check and try again.",
        "2.08": "Invalid card number entered. Please check and try again.",
        "2.09": "Invalid card expiry date. Please verify month and year.",
        "2.10": "3D Secure failed on merchant side. Please try again.",
        "2.11": "3D Secure failed on bank side. Please try again or use another card.",
        "2.12": "3D Secure authentication was incomplete. Please try again.",
        "2.13": "Invalid IP address. Please check and try again.",
        "2.14": "Subscription error. Please try again or contact support.",
        "2.15": "3D Secure is required. Please try again with authentication.",
        "2.16": "Subscription is currently pending. Please wait and try later.",
        "2.17": "Invalid coupon. Please try a valid one.",
        "2.18": "Invalid product ID. Please check and try again.",
        "3.01": "Card is blocked. Please contact your bank or try another card.",
        "3.02": "Insufficient funds. Please use another card.",
        "3.03": "Transaction amount exceeds card limits. Try another card.",
        "3.04": "Transaction declined by issuer. Try another card.",
        "3.05": "Please try another card.",
        "3.06": "This debit card is not supported. Use a credit card.",
        "3.07": "This card type is not supported. Please try another card.",
        "3.08": "Please try another card.",
        "3.09": "3D Secure authentication required. Please try again.",
        "3.10": "Please try another card.",
        "3.11": "Recurring payment cancelled. Please use another method.",
        "3.12": "Card or account is closed. Please try another card.",
        "4.01": "Please try another card.",
        "4.02": "Please try another card.",
        "4.03": "Card is restricted. Please contact your bank.",
        "4.04": "Please try another card.",
        "4.05": "Please try another card.",
        "4.07": "Please try another card.",
        "4.08": "Billing address mismatch. Please check and try again.",
        "4.09": "Please try another card.",
        "5.01": "3D Secure verification failed. Please try again.",
        "5.02": "Invalid card token. Please try again or use another card.",
        "5.03": "An error occurred. Please try again.",
        "5.04": "Merchant configuration error. Please contact support.",
        "5.05": "Merchant account not activated. Please contact support.",
        "5.06": "Duplicate transaction. Please wait and try again.",
        "5.07": "Too many requests. Please wait and try again.",
        "5.08": "Processing error. Please try again.",
        "5.09": "Merchant not found. Please contact support.",
        "5.10": "Unsupported API method. Please contact support.",
        "5.11": "Invalid routing configuration. Please contact support.",
        "5.12": "Account is blocked. Please contact support.",
        "6.01": "Please try another card.",
        "6.02": "Connection error. Please try again.",
        "6.03": "Processing error. Please try another card.",
        "7.01": "Card token not found. Please try again.",
        "7.02": "Google Pay failed. Please check details or try another method.",
        "7.03": "Smart cascade failure. Please try again.",
        "7.04": "Processing error. Please try again.",
        "7.05": "Apple Pay failed. Please try another method.",
        "7.06": "Token generation error. Please try again.",
        "7.07": "SCA engine error. Please try again or contact support.",

        unknown_error: "Payment failed. Please try again or use a different card."
    };

    return messages[code] ?? messages["unknown_error"];
}

export function getRandomItemFromArray<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
}
// Two decimals, except that the whole-unit currencies (INR, JPY, COP — see
// showsWholeUnits) drop a ".00" tail: "29.99", "₹1899", "¥6290".
// Two decimals, except that the whole-unit currencies (INR, JPY, COP — see
// showsWholeUnits) are rounded to the unit: a per-day ¥50.71 reads "¥51", the
// yen has no fraction to show.
export const formatMinorINRRule = (amount: number, currency: string) =>
    showsWholeUnits(currency) ? Math.round(amount).toString() : amount.toFixed(2);

// A price label longer than eight characters of sign and amount ("MX$559.00",
// "COP 156900", "AED 184.00") does not fit the upsell price slots sized for
// "$49.99" on a 320px phone; those slots set such a price smaller or stack it.
// Judged before render, from the strings the screen is about to print.
export const isLongPriceLabel = (currency: string, ...amounts: Array<number | null | undefined>): boolean =>
    amounts.some(
        (amount) =>
            amount != null && `${getCurrencySymbol(currency)}${formatMinorINRRule(amount, currency)}`.length > 8
    );

export const validatePasswords = (pass: string, confirmPass: string) => {
    if (pass.length < 6) {
        return "Password must be at least 6 characters long.";
    }
    if (confirmPass && pass !== confirmPass) {
        return "Passwords do not match.";
    }

    return "";
};

export const getPromptTitle = (
    model: string,
    blueIcon: boolean = false
): { title: string; name: string; img: string; mediaModel: boolean } => {
    switch (model) {
        case "gpt-4o":
            return {
                title: "ChatGPT",
                name: "ChatGPT",
                img: blueIcon ? "/images/providers/blue/ChatGPT.svg" : "/images/providers/openai.svg",
                mediaModel: false
            };
        case "gpt-5-nano":
            return {
                title: "ChatGPT",
                name: "ChatGPT",
                img: blueIcon ? "/images/providers/blue/ChatGPT.svg" : "/images/providers/openai.svg",
                mediaModel: false
            };
        case "gpt-5":
        case "gpt-5.1":
            return {
                title: "GPT-5.1",
                name: "GPT-5.1",
                img: blueIcon ? "/images/providers/blue/ChatGPT.svg" : "/images/providers/openai.svg",
                mediaModel: false
            };
        case "gpt-image-1":
        case "gpt-image-2":
            return {
                title: "ChatGPT Image",
                name: "ChatGPT Image",
                img: blueIcon ? "/images/providers/blue/ChatGPT.svg" : "/images/providers/openai.svg",
                mediaModel: true
            };
        case "tts-1":
            return {
                title: "Whisper",
                name: "Whisper",
                img: blueIcon ? "/images/providers/blue/ChatGPT.svg" : "/images/providers/openai.svg",
                mediaModel: false
            };
        case "deepseek-reasoner":
        case "deepseek-v4-flash":
            return {
                title: "Deepseek",
                name: "Deepseek",
                img: blueIcon ? "/images/providers/blue/DeepSeek.svg" : "/images/providers/deepseek.svg",
                mediaModel: false
            };
        case "gemini-2.0-flash":
        case "gemini-3.1-flash-lite":
            return {
                title: "Gemini",
                name: "Gemini",
                img: blueIcon ? "/images/providers/blue/Gemini.svg" : "/images/providers/google.svg",
                mediaModel: false
            };
        case "grok-3":
        case "grok-4.6":
            return {
                title: "Grok",
                name: "Grok",
                img: "/images/providers/grok.svg",
                mediaModel: false
            };
        case "claude-sonnet-4-5-20250929":
        case "claude-sonnet-5":
            return {
                title: "Claude",
                name: "Claude",
                img: "/images/providers/claude.svg",
                mediaModel: false
            };
        case "sonar":
            return {
                title: "Perplexity",
                name: "Perplexity",
                img: blueIcon ? "/images/providers/blue/Perplexity.svg" : "/images/providers/perplexity.svg",
                mediaModel: false
            };
        case "llama":
            return { title: "LLaMA", name: "LLaMA", img: "/images/providers/meta.svg", mediaModel: false };
        case "flux":
            return { title: "Flux", name: "Flux", img: "/images/providers/flux.svg", mediaModel: false };
        case "elevenlabs":
            return {
                title: "Elevenlabs",
                name: "Elevenlabs",
                img: "/images/providers/elevenlabs.svg",
                mediaModel: false
            };
        case "runway":
            return {
                title: "Runway",
                name: "Runway",
                img: blueIcon ? "/images/providers/blue/Runway.svg" : "/images/providers/runway.svg",
                mediaModel: true
            };
        case "wan":
            return {
                title: "Wan",
                name: "Wan",
                img: "/images/providers/wan.png",
                mediaModel: true
            };
        case "gemini-2.5-flash-image-preview":
        case "gemini-2.5-flash-image":
            return {
                title: "Nano Banana",
                name: "Nano Banana",
                img: blueIcon ? "/images/providers/blue/Gemini.svg" : "/images/providers/google.svg",
                mediaModel: true
            };
        default:
            return {
                title: "Prompt",
                name: "AI generated",
                img: blueIcon ? "/images/providers/blue/AI.svg" : "",
                mediaModel: false
            };
    }
};

export function getPlatform(): "WEB" | "MOB" {
    if (typeof navigator === "undefined") return "WEB";
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /android|ios|mobile/.test(userAgent);
    return isMobile ? "MOB" : "WEB";
}

export const returnToProfileOrHomePageByLocalStorageFlag = () => {
    const prevUrl = localStorage.getItem("prevUrl");

    localStorage.removeItem("first_login");
    localStorage.removeItem("purchase_payment_system");

    if (prevUrl) {
        return prevUrl;
    }

    return "/home";
};

export function copyText(value: string) {
    if (!value) return;

    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(value).catch(() => { });
        return;
    }
}



export const getOnboardingAnswerValueByProfileKey = (
    answers: UserOnboardingAnswer[] | null | undefined,
    profileKey: string
): string | number | any[] | undefined => {
    if (!Array.isArray(answers) || !profileKey) return undefined;

    const found = answers.find((item) => item?.profileKey === profileKey);
    const data = found?.data as unknown;

    if (Array.isArray(data)) {
        return data;
    }

    const value = (data as any)?.value as unknown;

    if (Array.isArray(value)) return value;
    if (typeof value === "string" || typeof value === "number") return value;

    return undefined;
};

/**
 * Provider marks drawn in black (or near-black) on transparent: inverted in dark mode so they stay
 * visible on the navy cards. Coloured marks (Claude, Gemini, DeepSeek, ...) are left alone.
 */
const DARK_INVERTED_PROVIDER_LOGOS = new Set([
    "/images/providers/openai.svg",
    "/images/providers/chat-gpt.svg",
    "/images/providers/elevenlabs.svg",
    "/images/providers/eleven-labs.svg",
    "/images/providers/fireworks.svg",
    "/images/providers/perplexity.svg",
    "/images/providers/grok.svg",
    "/images/providers/flux.svg",
    "/images/providers/runway.svg"
]);

/** Class to add to a provider logo <img>: "dark:invert" for the black marks, "" otherwise. */
export const providerLogoThemeClass = (img: string | undefined): string =>
    img && DARK_INVERTED_PROVIDER_LOGOS.has(img) ? "dark:invert" : "";
